import React, { useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfImage, sfSub } from './sectionHelpers';

interface Service {
  title: string;
  description: string;
  image_url?: string;
  features?: string[];
}

const AccordionServices: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const services = (c.services || []) as Service[];
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="sb-render sb-render-accordion-services" style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 12 }}
          >
            {sfHeadline(c, 'Our Services')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)' }}
          >
            {sfSub(c, 'Explore what we offer')}
          </InlineEditText>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {services.map((svc, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                style={{
                  borderTop: i === 0 ? '1px solid #e2e8f0' : undefined,
                  borderBottom: '1px solid #e2e8f0',
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <button
                  onClick={() => toggle(i)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '20px 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                  }}
                >
                  <span style={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: isOpen ? 'var(--brand-primary, #2563eb)' : '#0f172a',
                    transition: 'color 0.2s',
                  }}>
                    {svc.title || `Service ${i + 1}`}
                  </span>
                  <span style={{
                    fontSize: '1.5rem',
                    color: isOpen ? 'var(--brand-primary, #2563eb)' : '#94a3b8',
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s, color 0.2s',
                    flexShrink: 0,
                  }}>
                    +
                  </span>
                </button>

                {/* Collapsible body */}
                <div style={{
                  maxHeight: isOpen ? 800 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.35s ease',
                }}>
                  <div style={{
                    paddingBottom: 24,
                    display: 'grid',
                    gridTemplateColumns: sfImage(svc) ? '1fr 1fr' : '1fr',
                    gap: 32,
                    alignItems: 'start',
                  }}>
                    <div>
                      {svc.description && (
                        <p style={{ color: 'var(--brand-text-light, #64748b)', lineHeight: 1.7, marginBottom: 16 }}>
                          {svc.description}
                        </p>
                      )}
                      {svc.features && svc.features.length > 0 && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {svc.features.map((feat, fi) => (
                            <li key={fi} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.95rem' }}>
                              <span style={{ color: 'var(--brand-primary, #2563eb)', fontWeight: 700, fontSize: '1.1rem' }}>✓</span>
                              {feat}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {sfImage(svc) && (
                      <img
                        src={sfImage(svc)}
                        alt={svc.title}
                        style={{ width: '100%', borderRadius: 'var(--brand-card-radius, 12px)', objectFit: 'cover', maxHeight: 280 }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {services.length === 0 && (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
              No services configured yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default AccordionServices;
